import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cable, AlertTriangle, Mic2, Shuffle, Pencil, Check, RotateCcw } from "lucide-react";
import { padCh } from "./dawHelpers";
import { useMicAliases } from "@/lib/useMicAliases";

/**
 * RoutingMatrixModal — iter-204
 *
 * Full table view of the multi-track input routing.
 *   Rows = armed DAW channels
 *   Columns = current input source picker + status badges
 *
 * Why: the per-row dropdown in MixerRow is convenient for quick tweaks,
 * but when you've armed 8+ channels at once it's nice to see the entire
 * mapping at a glance — especially before pressing Record. This modal
 * also keeps duplicate detection front-and-centre with a warning band
 * across any rows that share the same input.
 *
 * Auto-swap behaviour is handled by the parent's onSourceChange (same
 * as the per-row picker), so picking Input N that's already used by
 * another armed channel will swap the two channels' inputs.
 *
 * iter-205 — Auto-balance button distributes armed channels round-robin
 * across all available inputs in one click. Lives in the header next to
 * the close button.
 *
 * iter-208 follow-up — Escape key now closes the modal (caught at the
 * document level so it works even when no inner element has focus).
 *
 * Session-only — no localStorage persistence by design (the user picked
 * "Keep it in-session only" during iter-204 scoping).
 */
export default function RoutingMatrixModal({
  open,
  onClose,
  armedChannels,         // Set<number>
  sourceMap,             // { [channelIdx]: inputSrc }
  channelNames = [],     // [string] — engine.channels.map(c => c.name)
  inputChannelCount = 1,
  onSourceChange,        // (channelIdx, newSrc) => void
  onAutoBalance,         // () => void  (iter-205)
}) {
  // iter-222 — per-input aliases (Mic 7 → SM7B / Lead Vocal / Kick).
  // Persisted to localStorage via useMicAliases.
  const { getLabel: getMicLabel, setAlias, clearAll: clearAllAliases } = useMicAliases();
  const [editingInput, setEditingInput] = useState(null); // index being renamed
  const [renameDraft, setRenameDraft] = useState("");
  const [showInputsPanel, setShowInputsPanel] = useState(false);
  // iter-208 follow-up — Esc closes the modal. Capture-phase listener
  // so a panel-deep input/select can't swallow the keystroke.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);
  if (!open) return null;
  const armedList = Array.from(armedChannels).sort((a, b) => a - b);
  // Detect duplicates so we can highlight them — even though auto-swap
  // should prevent these, a user might disarm a channel and re-arm
  // another that holds the same source, briefly creating a dup.
  const srcUsage = new Map();
  armedList.forEach((ch) => {
    const s = sourceMap[ch] ?? 0;
    if (!srcUsage.has(s)) srcUsage.set(s, []);
    srcUsage.get(s).push(ch);
  });
  const dupSources = new Set();
  for (const [src, chs] of srcUsage) if (chs.length > 1) dupSources.add(src);
  const usableInputs = Math.max(1, inputChannelCount);

  return (
    <AnimatePresence>
      <motion.div
        key="routing-matrix-overlay"
        data-testid="routing-matrix-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 12, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 12, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-card relative w-[min(720px,92vw)] max-h-[82vh] overflow-hidden rounded-3xl border border-fuchsia-400/15 bg-[#0c0d18]/95 p-0 shadow-[0_30px_70px_-20px_rgba(217,70,239,0.35)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] bg-gradient-to-r from-fuchsia-500/10 via-violet-500/5 to-transparent px-6 py-4">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-fuchsia-400 via-violet-500 to-cyan-400 shadow-[0_0_28px_rgba(168,85,247,0.5)]">
                <Cable className="h-5 w-5 text-white drop-shadow" />
              </span>
              <div>
                <h2 className="font-display text-base font-bold tracking-tight text-white">
                  Routing Matrix
                </h2>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-fuchsia-200/70">
                  Map mic inputs to armed channels
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="routing-matrix-auto-balance"
                onClick={onAutoBalance}
                disabled={armedList.length === 0}
                title={armedList.length === 0
                  ? "Arm one or more channels first"
                  : `Distribute the ${armedList.length} armed channel${armedList.length === 1 ? "" : "s"} round-robin across all ${usableInputs} input${usableInputs === 1 ? "" : "s"}`}
                className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-cyan-500/20 via-fuchsia-500/20 to-amber-500/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/90 ring-1 ring-white/15 transition hover:from-cyan-500/30 hover:via-fuchsia-500/30 hover:to-amber-500/30 hover:ring-white/30 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Shuffle className="h-3 w-3" />
                Auto-balance
              </button>
              <button
                type="button"
                data-testid="routing-matrix-close"
                onClick={onClose}
                className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white"
                aria-label="Close routing matrix"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-[64vh] overflow-y-auto px-6 py-5">
            {armedList.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-6 py-12 text-center">
                <Mic2 className="mx-auto h-8 w-8 text-white/40" />
                <p className="mt-3 font-display text-sm font-semibold text-white/80">
                  No channels armed yet
                </p>
                <p className="mt-1 text-xs text-white/55">
                  Arm one or more channels first, then come back here to map them
                  to specific mic inputs.
                </p>
              </div>
            ) : (
              <>
                {/* Interface info */}
                <div
                  data-testid="routing-matrix-interface-info"
                  className="mb-3 flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-2.5"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
                    Interface
                  </span>
                  <div className="flex items-center gap-3">
                    <span
                      data-testid="routing-matrix-input-count"
                      className="font-mono text-[11px] text-white/85"
                    >
                      {usableInputs} input{usableInputs === 1 ? "" : "s"} · {armedList.length} channel{armedList.length === 1 ? "" : "s"} armed
                    </span>
                    <button
                      type="button"
                      data-testid="routing-matrix-toggle-aliases"
                      onClick={() => setShowInputsPanel((v) => !v)}
                      className={`flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition ${
                        showInputsPanel
                          ? "bg-fuchsia-500/25 text-fuchsia-100 ring-1 ring-fuchsia-300/45"
                          : "bg-white/[0.06] text-white/70 hover:bg-white/[0.12] hover:text-white"
                      }`}
                      title="Rename inputs (e.g. Mic 7 → SM7B)"
                    >
                      <Pencil className="h-3 w-3" />
                      Rename inputs
                    </button>
                  </div>
                </div>

                {/* iter-222 — Per-input alias editor. Toggled by the
                    "Rename inputs" pill in the interface row. Each row
                    shows the default "Mic N" label + a text input with
                    the current alias. Empty input = clear the alias.
                    Persisted to localStorage via useMicAliases. */}
                {showInputsPanel && (
                  <div
                    data-testid="routing-matrix-aliases-panel"
                    className="mb-3 rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-fuchsia-200/75">
                        Audio Inputs · custom names
                      </span>
                      <button
                        type="button"
                        data-testid="routing-matrix-aliases-clear"
                        onClick={() => {
                          clearAllAliases();
                          setEditingInput(null);
                        }}
                        title="Reset every input back to Mic 1, Mic 2, …"
                        className="flex items-center gap-1 rounded-md bg-white/[0.05] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/65 transition hover:bg-white/[0.12] hover:text-white"
                      >
                        <RotateCcw className="h-2.5 w-2.5" />
                        Reset all
                      </button>
                    </div>
                    <ul className="grid grid-cols-2 gap-1.5">
                      {Array.from({ length: usableInputs }).map((_, i) => {
                        const isEditing = editingInput === i;
                        const currentLabel = getMicLabel(i);
                        const defaultLabel = `Mic ${i + 1}`;
                        const isAliased = currentLabel !== defaultLabel;
                        return (
                          <li
                            key={`alias-row-${i}`}
                            data-testid={`routing-alias-row-${i}`}
                            className="flex items-center gap-2 rounded-lg border border-white/[0.05] bg-black/30 px-2 py-1.5"
                          >
                            <span className="w-12 flex-shrink-0 font-mono text-[10px] text-white/55">
                              {defaultLabel}
                            </span>
                            {isEditing ? (
                              <>
                                <input
                                  data-testid={`routing-alias-input-${i}`}
                                  autoFocus
                                  value={renameDraft}
                                  onChange={(e) => setRenameDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      setAlias(i, renameDraft);
                                      setEditingInput(null);
                                    } else if (e.key === "Escape") {
                                      setEditingInput(null);
                                    }
                                  }}
                                  placeholder="e.g. SM7B"
                                  maxLength={28}
                                  className="flex-1 rounded bg-black/50 px-2 py-1 font-mono text-[11px] text-white outline-none ring-1 ring-fuchsia-400/40 focus:ring-fuchsia-300/80"
                                />
                                <button
                                  type="button"
                                  data-testid={`routing-alias-save-${i}`}
                                  onClick={() => {
                                    setAlias(i, renameDraft);
                                    setEditingInput(null);
                                  }}
                                  className="grid h-6 w-6 place-items-center rounded bg-emerald-500/25 text-emerald-200 hover:bg-emerald-500/40"
                                  title="Save"
                                >
                                  <Check className="h-3 w-3" />
                                </button>
                              </>
                            ) : (
                              <>
                                <span
                                  data-testid={`routing-alias-label-${i}`}
                                  className={`flex-1 truncate font-mono text-[11px] ${
                                    isAliased ? "text-fuchsia-100" : "text-white/45"
                                  }`}
                                >
                                  {isAliased ? currentLabel : "— no custom name —"}
                                </span>
                                <button
                                  type="button"
                                  data-testid={`routing-alias-edit-${i}`}
                                  onClick={() => {
                                    setRenameDraft(isAliased ? currentLabel : "");
                                    setEditingInput(i);
                                  }}
                                  className="grid h-6 w-6 place-items-center rounded bg-white/[0.06] text-white/65 hover:bg-fuchsia-500/25 hover:text-white"
                                  title="Rename this input"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                      Names persist across sessions · saved on this browser
                    </p>
                  </div>
                )}

                {/* Duplicate warning band */}
                {dupSources.size > 0 && (
                  <div
                    data-testid="routing-matrix-dup-warning"
                    className="mb-4 flex items-start gap-3 rounded-xl border border-rose-400/35 bg-rose-500/10 px-4 py-3"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-300" />
                    <div className="text-xs text-rose-100">
                      <p className="font-semibold">Duplicate input mapping</p>
                      <p className="mt-0.5 text-rose-200/80">
                        Multiple channels share the same input. Only the lowest
                        channel index will record; the others will be skipped.
                        Pick a different input or disarm one.
                      </p>
                    </div>
                  </div>
                )}

                {/* Table header */}
                <div className="mb-2 grid grid-cols-[80px_1fr_140px] items-center gap-3 px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                  <span>Channel</span>
                  <span>Name</span>
                  <span className="text-right">Input source</span>
                </div>

                {/* Rows */}
                <ul className="space-y-1.5">
                  {armedList.map((ch) => {
                    const src = sourceMap[ch] ?? 0;
                    const isDup = dupSources.has(src);
                    return (
                      <li
                        key={`routing-row-${ch}`}
                        data-testid={`routing-row-${ch}`}
                        className={`grid grid-cols-[80px_1fr_140px] items-center gap-3 rounded-xl border px-3 py-2.5 transition ${
                          isDup
                            ? "border-rose-400/40 bg-rose-500/[0.06]"
                            : "border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04]"
                        }`}
                      >
                        <span className="font-mono text-xs font-semibold text-white/90">
                          CH {padCh(ch)}
                        </span>
                        <span className="truncate text-sm text-white/75">
                          {channelNames[ch] || `Channel ${padCh(ch)}`}
                        </span>
                        <div className="flex justify-end">
                          <select
                            data-testid={`routing-row-src-${ch}`}
                            value={src}
                            onChange={(e) => onSourceChange?.(ch, Number(e.target.value))}
                            className={`h-8 cursor-pointer rounded-lg bg-black/40 px-2 font-mono text-xs text-white ring-1 focus:outline-none ${
                              isDup
                                ? "ring-rose-400/60"
                                : "ring-fuchsia-400/30 hover:ring-fuchsia-400/55"
                            }`}
                          >
                            {Array.from({ length: usableInputs }).map((_, i) => (
                              <option key={`routing-${ch}-opt-${i}`} value={i}>
                                {getMicLabel(i)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] bg-black/30 px-6 py-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
              Session only · resets on reload
            </span>
            <button
              type="button"
              data-testid="routing-matrix-done"
              onClick={onClose}
              className="rounded-xl bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-white shadow-[0_0_18px_rgba(168,85,247,0.45)] transition hover:brightness-110"
            >
              Done
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
